/** How long carrier registration takes, in days: the "about 2 days" every first-send note and the status page promise (user, 2026-10-23). */
export const REGISTRATION_WAIT_DAYS = 2;

/**
 * The message shown when a Work Order's send date/time resolves to the campaign-local past.
 * Mirrors the server's `SendDateTimeInPast` guard so the client blocks the save before the
 * PATCH (the server is still the backstop and returns its own 400 if the client ever slips).
 */
export const WORK_ORDER_SEND_IN_PAST_MESSAGE = 'The send date and time can’t be in the past.';

/**
 * Client-side mirror of the server's schedule guard: a send date/time may not be in the
 * campaign-local past. Compares campaign-local "YYYY-MM-DD"/"HH:mm" strings (both zero-padded,
 * so a lexicographic compare is a chronological one) — never `new Date()` / browser-local.
 *
 * Matches the server's end-of-day rule: with no explicit time, a date is treated as
 * end-of-day, so scheduling for *today* with no time is allowed. A future date is always
 * fine; a past date is always rejected; today's date is rejected only when an explicit time
 * has already passed.
 *
 * @param sendDate campaign-local send date "YYYY-MM-DD" (empty = unset → no error, it's optional)
 * @param sendTime campaign-local send time "HH:mm" (empty = unset)
 * @param todayStr campaign-local today "YYYY-MM-DD" (from `getCampaignTodayString()`)
 * @param nowTimeStr campaign-local current time "HH:mm" (from `getCampaignNowTimeString()`)
 * @returns the past-date message when invalid, otherwise null
 */
export function getWorkOrderScheduleError(
  sendDate: string,
  sendTime: string,
  todayStr: string,
  nowTimeStr: string,
): string | null {
  if (!sendDate) return null;
  if (sendDate < todayStr) return WORK_ORDER_SEND_IN_PAST_MESSAGE;
  if (sendDate === todayStr && sendTime && sendTime < nowTimeStr) {
    return WORK_ORDER_SEND_IN_PAST_MESSAGE;
  }
  return null;
}

/** A time zone's everyday name, such as "Central Time"; the id itself when the browser has no name for it. */
export function timeZoneName(timeZoneId: string | null | undefined): string | null {
  if (!timeZoneId) return null;
  try {
    return (
      new Intl.DateTimeFormat('en-US', { timeZone: timeZoneId, timeZoneName: 'longGeneric' })
        .formatToParts()
        .find((part) => part.type === 'timeZoneName')?.value ?? timeZoneId
    );
  } catch {
    return timeZoneId;
  }
}

/**
 * A campaign-local send date and time in words, such as "Tuesday, November 3 at 9:00 AM". The
 * values are already the campaign's wall-clock time, so they're formatted as given, never shifted
 * through the browser's zone.
 */
export function describeSendTime(sendDate: string | null, sendTime: string | null): string | null {
  if (!sendDate) return null;
  const [year, month, day] = sendDate.split('-').map(Number);
  const date = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(year, month - 1, day)),
  );
  if (!sendTime) return date;
  const [hour, minute] = sendTime.split(':').map(Number);
  const time = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }).format(
    new Date(Date.UTC(2000, 0, 1, hour, minute)),
  );
  return `${date} at ${time}`;
}
